import { api } from '../services/api';

/**
 * Trigger a browser file download from a Blob response
 */
function triggerDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Download invoice PDF for a booking
 * @param {number|string} bookingId
 * @param {string} [invoiceNumber] - optional, used in filename
 */
export async function downloadInvoice(bookingId, invoiceNumber) {
  if (!bookingId) throw new Error('Booking ID is required');
  try {
    const response = await api.getInvoice(bookingId);
    const filename = invoiceNumber
      ? `Invoice-${invoiceNumber}.pdf`
      : `Invoice-BK-${bookingId}.pdf`;
    triggerDownload(response.data, filename);
  } catch (err) {
    const msg = err.response?.data?.message || err.message || 'Failed to download invoice';
    throw new Error(msg);
  }
}

/**
 * Fetch invoice JSON data for preview
 * @param {number|string} bookingId
 * @returns {Promise<object>} { booking, customer, biller, items, total, company }
 */
export async function fetchInvoiceData(bookingId) {
  if (!bookingId) throw new Error('Booking ID is required');
  try {
    const response = await api.getInvoiceData(bookingId);
    return response.data;
  } catch (err) {
    const msg = err.response?.data?.message || err.message || 'Failed to fetch invoice data';
    throw new Error(msg);
  }
}

/**
 * Download monthly report PDF
 * @param {number} month  1–12
 * @param {number} year   e.g. 2026
 */
export async function downloadMonthlyReport(month, year) {
  if (!month || month < 1 || month > 12) throw new Error('Invalid month. Must be 1–12');
  if (!year) throw new Error('Year is required');
  try {
    const response = await api.getMonthlyReport(month, year);
    const mm = String(month).padStart(2, '0');
    triggerDownload(response.data, `Monthly-Report-${mm}-${year}.pdf`);
  } catch (err) {
    const msg = err.response?.data?.message || err.message || 'Failed to download monthly report';
    throw new Error(msg);
  }
}

/**
 * Download custom date-range report PDF
 * @param {string} startDate  YYYY-MM-DD
 * @param {string} endDate    YYYY-MM-DD
 */
export async function downloadCustomReport(startDate, endDate) {
  if (!startDate || !endDate) throw new Error('Start date and end date are required');
  if (new Date(startDate) > new Date(endDate))
    throw new Error('Start date must be before end date');
  try {
    const response = await api.getCustomReport(startDate, endDate);
    triggerDownload(response.data, `Report-${startDate}-to-${endDate}.pdf`);
  } catch (err) {
    const msg = err.response?.data?.message || err.message || 'Failed to download report';
    throw new Error(msg);
  }
}

/**
 * Format a number as INR currency  e.g. ₹1,234.00
 */
export function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
  }).format(amount || 0);
}

/**
 * Format a date string or Date object as DD/MM/YYYY
 */
export function formatDate(date) {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d)) return date;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
